import { useState } from 'react';
import { createTraitsSelector } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { useQuickstartUiStatus } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { QUICKSTART_UI_STATUS } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';

export const useIsSpaceConnected = (
	projectId: string | null | undefined,
): ReturnType<typeof createTraitsSelector<'SITE'>> =>
	createTraitsSelector<'SITE'>('SITE', ({ getTraitValue }) => {
		const isSpaceConnected = getTraitValue('jira-new-project-auto-connected-space');

		if (!isSpaceConnected || !projectId) return false;

		try {
			const connectedProjects = JSON.parse(isSpaceConnected);

			if (!Array.isArray(connectedProjects)) return false;

			return connectedProjects.includes(projectId);
		} catch (_: unknown) {
			return false;
		}
	});

export const useHasNoConfluenceOrHasConfluenceLessThan14days: ReturnType<
	typeof createTraitsSelector<'SITE'>
> = createTraitsSelector<'SITE'>('SITE', ({ getTraitValue }) => {
	const confluenceActivationDate = getTraitValue('confluence.ondemand.activationDate');
	// Does not have Confluence
	if (!confluenceActivationDate || typeof confluenceActivationDate !== 'string') return true;

	const today = new Date();
	const twoWeeksLaterActivation = new Date(confluenceActivationDate);
	twoWeeksLaterActivation.setDate(twoWeeksLaterActivation.getDate() + 14);
	// Has Confluence and has been active less than 14 days
	return today < twoWeeksLaterActivation;
});

/**
 * Determines if the user is eligible for the Pages Spotlight Space Connected experiment.
 * Eligibility criteria:
 * - Site has the jira-new-project-auto-connected-space TAP trait for this project
 */
export function useIsPagesSpotlightSpaceConnectedEligible(
	projectId: string | null | undefined,
): boolean {
	const [isSpaceConnected] = useIsSpaceConnected(projectId)();
	const [hasNoConfluenceOrHasConfluenceLessThan14days] =
		useHasNoConfluenceOrHasConfluenceLessThan14days();

	return isSpaceConnected && hasNoConfluenceOrHasConfluenceLessThan14days;
}

const statusDismissedSet = new Set([
	QUICKSTART_UI_STATUS.DISMISSED,
	QUICKSTART_UI_STATUS.MINIMIZED,
]);

/**
 * Determine if the quickstart has never been opened and is currently dismissed or minimized.
 */
export const useIsQuickstartDismissed = (): boolean => {
	const [quickstartStatus] = useQuickstartUiStatus();
	const [hasQuickstartBeenOpened, setHasQuickstartBeenOpened] = useState(false);
	const isOpen = quickstartStatus === QUICKSTART_UI_STATUS.OPEN;

	if (!hasQuickstartBeenOpened && isOpen) {
		setHasQuickstartBeenOpened(true);
	}

	return !hasQuickstartBeenOpened && statusDismissedSet.has(quickstartStatus);
};
